import _ from 'lodash';

import { LocalDate } from 'js-joda';
import { core, toJSONArray } from '@tock/coretypes';
import { dashboard } from 'types';
import AppConstants from 'constants/AppConstants';

export interface WaitlistFilters {
  ticketTypeId: number[];
  serviceType: core.Service.Type[];
  waitlistState?: core.Waitlist.State;
}

export interface BookingsReportFilters {
  businessId?: string[];
  menuItemId?: string[];
  state: string[];
  serviceType?: string[];
  guestTags?: string[];
  visitTags?: string[];
  ticketOptionId?: string[];
  ticketTypeId?: number[];
}

export interface State {
  bookingsReportFilters: BookingsReportFilters;
  isInitialized: boolean;
  requestInProgress: boolean;
  endDate: string | null;
  startDate: string | null;
  transactionRows: dashboard.TransactionReportRow[];
  redemptionRows: dashboard.RedemptionReportRow[];
  postVisitFeedbackRows: dashboard.PostVisitFeedbackReportRow[];
  serviceRows: dashboard.TableReportRow[];
  guestRows: dashboard.GuestReportRow[];
  tableManagementRows: dashboard.PartyVisit[];
  waitlistRows: dashboard.WaitlistReportRow[];
  selectedWaitlistReportRow?: dashboard.WaitlistReportRow;
  waitlistReportFilter: WaitlistFilters;
  waitlistReportTextFilter?: string;
  tablesReportFilterHasExperienceSelected: boolean;
}

export const initialState: State = {
  bookingsReportFilters: {
    state: ['SOLD'],
  },
  isInitialized: false,
  requestInProgress: false,
  endDate: LocalDate.now().toString(),
  startDate: LocalDate.now().toString(),
  transactionRows: [],
  redemptionRows: [],
  postVisitFeedbackRows: [],
  serviceRows: [],
  guestRows: [],
  tableManagementRows: [],
  waitlistRows: [],
  waitlistReportFilter: {
    ticketTypeId: [],
    serviceType: [],
    waitlistState: 'ACTIVE',
  },
  selectedWaitlistReportRow: undefined,
  tablesReportFilterHasExperienceSelected: false,
};

export default function reducer(state: State = initialState, action: BaseTockAction): State {
  const { type, payload } = action;

  switch (type) {
    case AppConstants.REPORT_TABLES_FETCH_DONE: {
      return {
        ...state,
        serviceRows: toJSONArray<dashboard.TableReportRow>(payload),
        isInitialized: true,
        requestInProgress: false,
      };
    }
    case AppConstants.REPORT_TRANSACTIONS_FETCH_DONE: {
      return {
        ...state,
        transactionRows: toJSONArray<dashboard.TransactionReportRow>(payload),
        isInitialized: true,
        requestInProgress: false,
      };
    }
    case AppConstants.REPORT_REDEMPTIONS_FETCH_DONE: {
      return {
        ...state,
        redemptionRows: toJSONArray<dashboard.RedemptionReportRow>(payload),
        isInitialized: true,
        requestInProgress: false,
      };
    }
    case AppConstants.REPORT_FEEDBACK_FETCH_DONE: {
      return {
        ...state,
        postVisitFeedbackRows: toJSONArray<dashboard.PostVisitFeedbackReportRow>(payload),
        isInitialized: true,
        requestInProgress: false,
      };
    }
    case AppConstants.REPORT_GUESTS_FETCH_DONE: {
      return {
        ...state,
        guestRows: payload,
        isInitialized: true,
        requestInProgress: false,
      };
    }
    case AppConstants.REPORT_TABLE_MANAGEMENT_FETCH_DONE: {
      return {
        ...state,
        tableManagementRows: toJSONArray<dashboard.PartyVisit>(payload),
        isInitialized: true,
        requestInProgress: false,
      };
    }
    case AppConstants.REPORT_TABLE_MANAGEMENT_FETCH_UPDATE_PURCHASE: {
      const purchase = payload as core.IPastPurchase;
      const rowToUpdateIndex = state.tableManagementRows.findIndex(
        (row) => row.bookingInfo?.purchaseId === purchase.id
      );

      if (rowToUpdateIndex === -1) {
        return state;
      }

      const rowToUpdate = state.tableManagementRows[rowToUpdateIndex];
      if (!rowToUpdate.bookingInfo) {
        return state;
      }

      // We get the whole purchase in the response, but this is the only stuff that changes
      rowToUpdate.bookingInfo.paymentCardHold = purchase.paymentCardHold;
      rowToUpdate.bookingInfo.isEligibleForBookingPolicyFee =
        purchase.isEligibleForBookingPolicyFee;
      const tableManagementRows = [
        ...state.tableManagementRows.slice(0, rowToUpdateIndex),
        rowToUpdate,
        ...state.tableManagementRows.slice(rowToUpdateIndex + 1),
      ];
      return {
        ...state,
        tableManagementRows,
      };
    }
    case AppConstants.REPORT_WAITLISTS_FETCH_DONE: {
      return {
        ...state,
        waitlistRows: handleServiceType(payload),
        isInitialized: true,
        requestInProgress: false,
      };
    }
    case AppConstants.REPORT_SET_DATA: {
      return {
        ...state,
        ...payload,
        requestInProgress: !_.isNil(payload.startDate) && !_.isNil(payload.endDate),
      };
    }
    case AppConstants.REPORT_TABLES_FETCH_STARTING:
    case AppConstants.REPORT_TRANSACTIONS_FETCH_STARTING:
    case AppConstants.REPORT_REDEMPTIONS_FETCH_STARTING:
    case AppConstants.REPORT_FEEDBACK_FETCH_STARTING:
    case AppConstants.REPORT_GUESTS_FETCH_STARTING:
    case AppConstants.REPORT_TABLE_MANAGEMENT_FETCH_STARTING:
    case AppConstants.REPORT_WAITLISTS_FETCH_STARTING:
    case AppConstants.DOWNLOAD:
      return {
        ...state,
        requestInProgress: true,
      };
    case AppConstants.REPORT_TABLES_FETCH_FAILED:
    case AppConstants.REPORT_TRANSACTIONS_FETCH_FAILED:
    case AppConstants.REPORT_REDEMPTIONS_FETCH_FAILED:
    case AppConstants.REPORT_GUESTS_FETCH_FAILED:
    case AppConstants.REPORT_TABLE_MANAGEMENT_FETCH_FAILED:
    case AppConstants.REPORT_WAITLISTS_FETCH_FAILED:
      return {
        ...state,
        transactionRows: [],
        redemptionRows: [],
        postVisitFeedbackRows: [],
        guestRows: [],
        tableManagementRows: [],
        waitlistRows: [],
        requestInProgress: false,
      };
    case AppConstants.REPORT_TABLES_REPORT_HAS_EXPERIENCE_SELECTED:
      return {
        ...state,
        tablesReportFilterHasExperienceSelected: payload,
      };
    case AppConstants.SET_BOOKINGS_REPORT_FILTERS:
      return {
        ...state,
        bookingsReportFilters: {
          ...payload,
        },
      };
    case AppConstants.SET_WAITLIST_REPORT_FILTER:
      return {
        ...state,
        waitlistReportFilter: payload,
      };
    case AppConstants.SET_WAITLIST_REPORT_TEXT_FILTER:
      return {
        ...state,
        waitlistReportTextFilter: payload,
      };
    case AppConstants.UI_TOGGLE_WAITLIST_REPORT_ROW_MODAL: {
      return {
        ...state,
        selectedWaitlistReportRow: payload.waitlistReportRow,
      };
    }

    default:
      return state;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleServiceType(payload: any[]) {
  return _.map(payload, (item) => {
    if (!item.serviceType) {
      item.serviceType = null;
    }
    return item;
  });
}
